import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, inject, OnInit, OnDestroy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { Subject, takeUntil, timeout } from "rxjs";

import { MasterPrintMasterServices } from "src/app/Common/PrintMaster/PrintMaster.service";
import {
  PrintMasterPrintDynamicSQLRequestDTO,
  DynamicSqlTemplateGenerationRequestDTO,
  DynamicSqlTemplateGenerationResponseDTO,
  UpdateSqlTemplateRequestDTO,
  UpdateSqlTemplateResponseDTO
} from "src/app/Common/PrintMaster/PrintMaster.interface";
import { SchoolMasterService } from "src/app/School/BackOffice/school-master/school-master.service";
import { HospitalMasterService } from "src/app/hospital/BackOffice/hospital-master/hospital-master.service";
import { UserLoginService } from "src/app/login/userlogin.service";
import { environment } from "src/environment/environment";

interface PrintMasterGeneratePrintRequestDTO {
  moduleName: string;
  moduleId: string;
  templateId: string | null;
  headerHtml: string | null;
  customCss: string | null;
  footerHtml: string | null;
}

interface PrintMasterGeneratePrintResponseDTO {
  moduleName?: string | null;
  moduleId?: string | null;
  templateName?: string | null;
  pSeq: number;
  pContent?: string | null;
}

interface PrintMasterGeneratePrintList {
  items?: PrintMasterGeneratePrintResponseDTO[] | null;
}

@Component({
  selector: "app-generate-certificate",
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: "./generate-certificate.component.html",
  styleUrls: ["./generate-certificate.component.css"],
})
export class GenerateCertificateComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private toastr = inject(ToastrService);
  private fb = inject(FormBuilder);
  private masterPrintMasterService = inject(MasterPrintMasterServices);
  private schoolMasterService = inject(SchoolMasterService);
  private hospitalMasterService = inject(HospitalMasterService);
  private userLoginService = inject(UserLoginService);

  readonly moduleOptions = [
    { value: "School", label: "School" },
    { value: "Hospital", label: "Hospital" },
    { value: "Society", label: "Housing Society" },
  ];

  // Tab switching: 'render' (existing certificate flow) or 'aiTemplate' (new AI flow)
  activeTab: 'render' | 'aiTemplate' = 'render';

  // --- Render Certificate Tab State ---
  selectedModuleName: string = "";
  selectedEntityId: number = 0;
  selectedTemplateId: string = "";

  entityList: any[] = [];
  templateList: string[] = [];

  isEntityLoading: boolean = false;
  isTemplateLoading: boolean = false;
  isGenerating: boolean = false;

  headerHtml: string = "";
  customCss: string = "";
  footerHtml: string = "";

  generatedCertificateHtml: string = "";

  readonly cssPlaceholder: string =
    "body { font-family: Arial, sans-serif; }\n" +
    ".certificate-header { text-align: center; }";

  readonly footerPlaceholder: string =
    '<div class="certificate-footer">\n' +
    "  <p>Authorized Signatory</p>\n" +
    "</div>";

  // --- AI Generator Tab State ---
  templateMode: 'new' | 'modify' = 'new'; // 'new' = generate new template, 'modify' = modify existing template

  selectedAiModuleName: string = "";
  selectedAiEntityId: number = 0;
  selectedAiTemplateType: string = "";

  aiEntityList: any[] = [];
  aiTemplateList: string[] = [];
  isAiEntityLoading: boolean = false;
  isAiTemplateLoading: boolean = false;

  // Existing template inspection
  isCheckingExistingTemplate: boolean = false;
  existingTemplateFound: boolean = false;
  existingPrintId: number = 0;
  existingTemplateSql: string = "";

  // Form & File
  aiForm!: FormGroup;
  selectedHtmlFile: File | null = null;
  aiCustomPrompt: string = "";
  uploadedFileName: string = "";
  uploadedFileSizeKb: number = 0;
  uploadedHtmlContent: string = "";
  fileUploadError: string = "";

  // Loading animation state
  isGeneratingAi: boolean = false;
  loadingPhase: number = 1;
  loadingProgress: number = 0;
  loadingPhaseText: string = "";
  private loadingTimer: any = null;

  // Aliases for unit tests
  get customPromptControl(): FormControl {
    return this.aiForm.get('customPrompt') as FormControl;
  }

  get isAiGenerating(): boolean { return this.isGeneratingAi; }
  set isAiGenerating(val: boolean) { this.isGeneratingAi = val; }

  aiErrorMessage: string = "";
  aiSuccessResult: any = null;
  get generationResult(): any { return this.aiSuccessResult; }
  set generationResult(val: any) { this.aiSuccessResult = val; }

  // Result preview
  previewCertificateHtml: string = "";
  isPreviewLoading: boolean = false;
  isSqlPreviewCollapsed: boolean = true;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initAiForm();
    this.prefillUserModuleContext();
  }

  ngOnDestroy(): void {
    this.stopLoadingAnimation();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initAiForm(): void {
    this.aiForm = this.fb.group({
      moduleName: ["", Validators.required],
      moduleId: [null, [Validators.required, Validators.min(1)]],
      masterId: [null],
      masterType: ["", Validators.required],
      currentTemplateHTML: [""],
      customPrompt: [""],
    });
  }

  private prefillUserModuleContext(): void {
    try {
      const user = this.userLoginService.User();
      if (user) {
        const id = user.companyId || user.loggedInBranchId || 0;
        if (id > 0) {
          this.selectedEntityId = id;
          this.selectedAiEntityId = id;
          this.aiForm.patchValue({ moduleId: id, masterId: id });
        }
      }
    } catch {
      // Ignored if user session is not available
    }
  }

  // ==============================================================
  // TAB 1: RENDER CERTIFICATE HANDLERS
  // ==============================================================

  onModuleChange(): void {
    this.selectedEntityId = 0;
    this.selectedTemplateId = "";
    this.entityList = [];
    this.templateList = [];
    this.generatedCertificateHtml = "";
    this.headerHtml = "";
    this.customCss = "";
    this.footerHtml = "";

    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    if (!moduleName) return;

    this.loadEntityList();
    this.loadTemplateTypes();
  }

  onEntityChange(): void {
    this.generatedCertificateHtml = "";
  }

  onTemplateChange(): void {
    this.generatedCertificateHtml = "";
  }

  loadEntityList(): void {
    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    if (!moduleName || this.isEntityLoading) return;

    this.isEntityLoading = true;
    this.entityList = [];

    const payload = {
      pageSize: 1000,
      pageNo: 1,
      orderByClause: "PrimaryDesc",
      whereClause: undefined,
      userId: null,
      roleId: 1,
    };

    let request: any;
    if (moduleName === "School") {
      request = this.schoolMasterService.SchoolMaster_ReadAllPaginated(payload);
    } else if (moduleName === "Hospital") {
      const url = `${String(environment.hmApiURL || "").replace(/\/$/, "")}/H/HM/ReadAllPaginated`;
      request = this.http.post<any>(url, payload);
    } else {
      const url = `${String(environment.smApiURL || "").replace(/\/$/, "")}/SOC/SOC/ReadAllPaginated`;
      request = this.http.post<any>(url, payload);
    }

    request.subscribe({
      next: (res: any) => {
        const allEntities = this.getResponseItems(res);
        const activeEntities = allEntities.filter((entity: any) => {
          const isActive = Number(entity?.isActive ?? entity?.IsActive ?? 1);
          const isDeleted = Number(entity?.isDeleted ?? entity?.IsDeleted ?? 0);
          return isActive === 1 && isDeleted === 0;
        });

        const onboardedEntities = activeEntities.filter((entity: any) => {
          const status = String(entity?.onBoardingStatus ?? entity?.OnBoardingStatus ?? "").trim().toLowerCase();
          return status === "completed" || status === "complete" || status === "onboarded" || status === "onboarding completed";
        });

        this.entityList = onboardedEntities.length > 0 ? onboardedEntities : activeEntities;
        this.isEntityLoading = false;

        if (this.entityList.length === 1) {
          this.selectedEntityId = this.getEntityId(this.entityList[0]);
        }
      },
      error: (err: any) => {
        console.error("Certificate entity paginated load error:", err);
        this.entityList = [];
        this.isEntityLoading = false;
        this.toastr.error(`Unable to load ${this.entityLabel.toLowerCase()} list.`);
      },
    });
  }

  loadTemplateTypes(): void {
    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    if (!moduleName || this.isTemplateLoading) return;

    this.isTemplateLoading = true;
    this.templateList = [];

    let request: any;
    if (moduleName === "School") {
      const url = `${String(environment.speApiURL || "").replace(/\/$/, "")}/SCL/PM/ReadBySchoolId`;
      request = this.http.post<any>(url, { schoolId: 0 });
    } else if (moduleName === "Hospital") {
      const url = `${String(environment.hmApiURL || "").replace(/\/$/, "")}/H/PM/ReadByHospitalId`;
      request = this.http.post<any>(url, { hospitalId: 0 });
    } else {
      const url = `${String(environment.smApiURL || "").replace(/\/$/, "")}/SOC/PM/ReadBySocietyId`;
      request = this.http.post<any>(url, { societyId: 0 });
    }

    request.subscribe({
      next: (res: any) => {
        const uniqueTypes = new Set<string>();
        this.getResponseItems(res).forEach((item: any) => {
          const masterType = String(item?.masterType ?? item?.MasterType ?? "").trim();
          const isActive = Number(item?.isActive ?? item?.IsActive ?? 1);
          const isDeleted = Number(item?.isDeleted ?? item?.IsDeleted ?? 0);
          if (masterType && isActive === 1 && isDeleted === 0) {
            uniqueTypes.add(masterType);
          }
        });

        this.templateList = Array.from(uniqueTypes).sort((a, b) => a.localeCompare(b));
        if (this.templateList.length === 1) {
          this.selectedTemplateId = this.templateList[0];
        }
        this.isTemplateLoading = false;
      },
      error: (err: any) => {
        console.error("PrintMaster template type load error:", err);
        this.templateList = [];
        this.isTemplateLoading = false;
        this.toastr.error("Unable to load template types.");
      },
    });
  }

  generateCertificate(): void {
    if (this.isGenerating) return;

    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    if (!moduleName) {
      this.toastr.warning("Please select a Module.");
      return;
    }

    const moduleId = Number(this.selectedEntityId || 0);
    if (moduleId <= 0) {
      this.toastr.warning(`Please select a ${this.entityLabel.toLowerCase()}.`);
      return;
    }

    const templateId = String(this.selectedTemplateId || "").trim();
    if (!templateId) {
      this.toastr.warning("Please select a Template Type.");
      return;
    }

    this.isGenerating = true;
    this.generatedCertificateHtml = "";

    const generatePrintPayload: PrintMasterGeneratePrintRequestDTO = {
      moduleName: moduleName,
      moduleId: moduleId.toString(),
      templateId: templateId || null,
      headerHtml: this.headerHtml?.trim() ? this.headerHtml : null,
      customCss: this.customCss?.trim() ? this.customCss : null,
      footerHtml: this.footerHtml?.trim() ? this.footerHtml : null,
    };

    let generateUrl: string;
    if (moduleName === "School") {
      generateUrl = `${String(environment.speApiURL || "").replace(/\/$/, "")}/SCL/PM/GeneratePrint`;
    } else if (moduleName === "Hospital") {
      generateUrl = `${String(environment.hmApiURL || "").replace(/\/$/, "")}/H/PM/GeneratePrint`;
    } else {
      generateUrl = `${String(environment.smApiURL || "").replace(/\/$/, "")}/SOC/PM/GeneratePrint`;
    }

    this.http.post<PrintMasterGeneratePrintList>(generateUrl, generatePrintPayload).subscribe({
      next: (generateRes) => {
        const rawTemplateSql = this.extractTemplateSql(generateRes);
        if (!rawTemplateSql) {
          this.isGenerating = false;
          this.toastr.error("No valid print template SQL returned for selected options.");
          return;
        }

        const preparedDynamicSql = this.applyModuleTokens(rawTemplateSql);
        const dynamicSqlPayload: PrintMasterPrintDynamicSQLRequestDTO = {
          dynamicSQL: preparedDynamicSql,
        };

        this.masterPrintMasterService.PrintMaster_PrintDynamicSQL(dynamicSqlPayload).subscribe({
          next: (renderRes) => {
            this.isGenerating = false;
            const finalHtml = this.getPrintHtml(renderRes);
            if (!finalHtml.trim()) {
              this.toastr.warning("Template returned empty rendered output.");
              return;
            }
            this.generatedCertificateHtml = finalHtml;
            this.toastr.success("Certificate generated successfully.");
          },
          error: (renderError: any) => {
            console.error("Certificate PrintDynamicSQL render error:", renderError);
            this.isGenerating = false;
            this.toastr.error(renderError?.error?.message || renderError?.message || "Unable to render certificate.");
          },
        });
      },
      error: (generateError: any) => {
        console.error("GeneratePrint API error:", generateError);
        this.isGenerating = false;
        this.toastr.error(generateError?.error?.message || generateError?.message || "Unable to generate certificate template.");
      },
    });
  }

  viewGeneratedCertificate(): void {
    if (!this.generatedCertificateHtml?.trim()) {
      this.toastr.warning("Please generate the certificate first.");
      return;
    }
    const finalHtml = this.buildPreviewDocument(this.generatedCertificateHtml);
    const blob = new Blob([finalHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (!newWindow) {
      URL.revokeObjectURL(url);
      this.toastr.warning("Please allow popups to view the certificate.");
      return;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  downloadGeneratedCertificate(): void {
    if (!this.generatedCertificateHtml?.trim()) {
      this.toastr.warning("Please generate the certificate first.");
      return;
    }
    const finalHtml = this.buildPreviewDocument(this.generatedCertificateHtml);
    const blob = new Blob([finalHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const entityName = this.getEntityName(this.getSelectedEntity());
    anchor.href = url;
    anchor.download = `${this.toSafeFileName(entityName || this.entityLabel)}-${this.toSafeFileName(this.selectedTemplateId || "Certificate")}.html`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  // ==============================================================
  // TAB 2: AI DYNAMIC SQL TEMPLATE GENERATOR
  // ==============================================================

  onAiModuleChange(): void {
    this.selectedAiEntityId = 0;
    this.selectedAiTemplateType = "";
    this.aiEntityList = [];
    this.aiTemplateList = [];
    this.aiSuccessResult = null;
    this.aiErrorMessage = "";
    this.previewCertificateHtml = "";
    this.existingTemplateFound = false;
    this.existingPrintId = 0;
    this.existingTemplateSql = "";

    this.aiForm.patchValue({
      moduleName: this.selectedAiModuleName,
      moduleId: null,
      masterId: null,
      masterType: ""
    });

    const moduleName = this.normalizeModuleName(this.selectedAiModuleName);
    if (!moduleName) return;

    this.loadAiEntityList();
    this.loadAiTemplateList();
  }

  onAiEntityChange(): void {
    this.aiSuccessResult = null;
    this.aiErrorMessage = "";
    this.previewCertificateHtml = "";

    if (this.selectedAiEntityId > 0) {
      this.aiForm.patchValue({
        moduleId: this.selectedAiEntityId,
        masterId: this.selectedAiEntityId
      });
    }

    if (this.templateMode === 'modify') {
      this.checkExistingTemplate();
    }
  }

  onAiTemplateChange(): void {
    this.aiSuccessResult = null;
    this.aiErrorMessage = "";
    this.previewCertificateHtml = "";

    if (this.selectedAiTemplateType) {
      this.aiForm.patchValue({
        masterType: this.selectedAiTemplateType
      });
    }

    if (this.templateMode === 'modify') {
      this.checkExistingTemplate();
    }
  }

  setTemplateMode(mode: 'new' | 'modify'): void {
    this.templateMode = mode;
    this.aiSuccessResult = null;
    this.aiErrorMessage = "";
    this.previewCertificateHtml = "";

    if (mode === 'modify') {
      this.checkExistingTemplate();
    }
  }

  loadAiEntityList(): void {
    const moduleName = this.normalizeModuleName(this.selectedAiModuleName);
    if (!moduleName || this.isAiEntityLoading) return;

    this.isAiEntityLoading = true;
    this.aiEntityList = [];

    const payload = {
      pageSize: 1000,
      pageNo: 1,
      orderByClause: "PrimaryDesc",
      whereClause: undefined,
      userId: null,
      roleId: 1,
    };

    let request: any;
    if (moduleName === "School") {
      request = this.schoolMasterService.SchoolMaster_ReadAllPaginated(payload);
    } else if (moduleName === "Hospital") {
      const url = `${String(environment.hmApiURL || "").replace(/\/$/, "")}/H/HM/ReadAllPaginated`;
      request = this.http.post<any>(url, payload);
    } else {
      const url = `${String(environment.smApiURL || "").replace(/\/$/, "")}/SOC/SOC/ReadAllPaginated`;
      request = this.http.post<any>(url, payload);
    }

    request.subscribe({
      next: (res: any) => {
        const allEntities = this.getResponseItems(res);
        const activeEntities = allEntities.filter((entity: any) => {
          const isActive = Number(entity?.isActive ?? entity?.IsActive ?? 1);
          const isDeleted = Number(entity?.isDeleted ?? entity?.IsDeleted ?? 0);
          return isActive === 1 && isDeleted === 0;
        });

        const onboardedEntities = activeEntities.filter((entity: any) => {
          const status = String(entity?.onBoardingStatus ?? entity?.OnBoardingStatus ?? "").trim().toLowerCase();
          return status === "completed" || status === "complete" || status === "onboarded" || status === "onboarding completed";
        });

        this.aiEntityList = onboardedEntities.length > 0 ? onboardedEntities : activeEntities;
        this.isAiEntityLoading = false;

        if (this.aiEntityList.length === 1) {
          this.selectedAiEntityId = this.getEntityId(this.aiEntityList[0]);
          this.aiForm.patchValue({
            moduleId: this.selectedAiEntityId,
            masterId: this.selectedAiEntityId
          });
          if (this.templateMode === 'modify') {
            this.checkExistingTemplate();
          }
        }
      },
      error: (err: any) => {
        console.error("AI entity load error:", err);
        this.aiEntityList = [];
        this.isAiEntityLoading = false;
        this.toastr.error(`Unable to load ${this.aiEntityLabel.toLowerCase()} list.`);
      },
    });
  }

  loadAiTemplateList(): void {
    const moduleName = this.normalizeModuleName(this.selectedAiModuleName);
    if (!moduleName || this.isAiTemplateLoading) return;

    this.isAiTemplateLoading = true;
    this.aiTemplateList = [];

    let request: any;
    if (moduleName === "School") {
      const url = `${String(environment.speApiURL || "").replace(/\/$/, "")}/SCL/PM/ReadBySchoolId`;
      request = this.http.post<any>(url, { schoolId: 0 });
    } else if (moduleName === "Hospital") {
      const url = `${String(environment.hmApiURL || "").replace(/\/$/, "")}/H/PM/ReadByHospitalId`;
      request = this.http.post<any>(url, { hospitalId: 0 });
    } else {
      const url = `${String(environment.smApiURL || "").replace(/\/$/, "")}/SOC/PM/ReadBySocietyId`;
      request = this.http.post<any>(url, { societyId: 0 });
    }

    request.subscribe({
      next: (res: any) => {
        const uniqueTypes = new Set<string>();
        this.getResponseItems(res).forEach((item: any) => {
          const masterType = String(item?.masterType ?? item?.MasterType ?? "").trim();
          const isActive = Number(item?.isActive ?? item?.IsActive ?? 1);
          const isDeleted = Number(item?.isDeleted ?? item?.IsDeleted ?? 0);
          if (masterType && isActive === 1 && isDeleted === 0) {
            uniqueTypes.add(masterType);
          }
        });

        this.aiTemplateList = Array.from(uniqueTypes).sort((a, b) => a.localeCompare(b));
        if (this.aiTemplateList.length === 1) {
          this.selectedAiTemplateType = this.aiTemplateList[0];
          this.aiForm.patchValue({ masterType: this.selectedAiTemplateType });
          if (this.templateMode === 'modify') {
            this.checkExistingTemplate();
          }
        }
        this.isAiTemplateLoading = false;
      },
      error: (err: any) => {
        console.error("AI template type load error:", err);
        this.aiTemplateList = [];
        this.isAiTemplateLoading = false;
        this.toastr.error("Unable to load template types.");
      },
    });
  }

  checkExistingTemplate(): void {
    const moduleName = this.normalizeModuleName(this.selectedAiModuleName);
    const entityId = Number(this.selectedAiEntityId || 0);
    const templateType = String(this.selectedAiTemplateType || "").trim();

    if (!moduleName || entityId <= 0 || !templateType) {
      this.existingTemplateFound = false;
      this.existingPrintId = 0;
      this.existingTemplateSql = "";
      return;
    }

    this.isCheckingExistingTemplate = true;

    let request: any;
    if (moduleName === "School") {
      const url = `${String(environment.speApiURL || "").replace(/\/$/, "")}/SCL/PM/ReadBySchoolId`;
      request = this.http.post<any>(url, { schoolId: entityId });
    } else if (moduleName === "Hospital") {
      const url = `${String(environment.hmApiURL || "").replace(/\/$/, "")}/H/PM/ReadByHospitalId`;
      request = this.http.post<any>(url, { hospitalId: entityId });
    } else {
      const url = `${String(environment.smApiURL || "").replace(/\/$/, "")}/SOC/PM/ReadBySocietyId`;
      request = this.http.post<any>(url, { societyId: entityId });
    }

    request.subscribe({
      next: (res: any) => {
        this.isCheckingExistingTemplate = false;
        const items = this.getResponseItems(res);
        const match = items.find((item: any) => {
          const mType = String(item?.masterType ?? item?.MasterType ?? "").trim();
          const isActive = Number(item?.isActive ?? item?.IsActive ?? 1);
          const isDeleted = Number(item?.isDeleted ?? item?.IsDeleted ?? 0);
          return mType.toLowerCase() === templateType.toLowerCase() && isActive === 1 && isDeleted === 0;
        });

        if (match) {
          this.existingTemplateFound = true;
          this.existingPrintId = Number(match?.printId ?? match?.PrintId ?? 0);
          this.existingTemplateSql = String(match?.pContent ?? match?.PContent ?? "");
        } else {
          this.existingTemplateFound = false;
          this.existingPrintId = 0;
          this.existingTemplateSql = "";
        }
      },
      error: () => {
        this.isCheckingExistingTemplate = false;
        this.existingTemplateFound = false;
        this.existingPrintId = 0;
        this.existingTemplateSql = "";
      }
    });
  }

  // --- HTML File Upload Handlers ---
  onHtmlFileSelected(event: any): void {
    const input = event?.target as HTMLInputElement;
    if (!input?.files || input.files.length === 0) return;

    const file = input.files[0];
    const fileName = file.name || "";
    const ext = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();

    if (ext !== ".html" && ext !== ".htm") {
      this.fileUploadError = "Only .html or .htm files are accepted.";
      this.clearSelectedFile(input);
      this.toastr.warning(this.fileUploadError, "Invalid File");
      return;
    }

    const sizeKb = file.size / 1024;
    const maxKb = 500;
    if (sizeKb > maxKb) {
      this.fileUploadError = `File size exceeds the 500 KB limit.`;
      this.clearSelectedFile(input);
      this.toastr.error(this.fileUploadError, "File Too Large");
      return;
    }

    this.fileUploadError = "";
    this.selectedHtmlFile = file;
    this.uploadedFileName = fileName;
    this.uploadedFileSizeKb = Math.round(sizeKb * 10) / 10;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.uploadedHtmlContent = (e.target?.result as string) || "";
      this.aiForm.patchValue({ currentTemplateHTML: this.uploadedHtmlContent });
    };
    reader.onerror = () => {
      this.fileUploadError = "Failed to read HTML file.";
      this.toastr.error(this.fileUploadError);
    };
    reader.readAsText(file);
  }

  clearSelectedFile(inputElement?: HTMLInputElement): void {
    this.selectedHtmlFile = null;
    this.uploadedFileName = "";
    this.uploadedFileSizeKb = 0;
    this.uploadedHtmlContent = "";
    this.fileUploadError = "";
    this.aiForm.patchValue({ currentTemplateHTML: "" });
    if (inputElement) inputElement.value = "";
  }

  // Alias for backward compatibility
  clearUploadedFile(inputElement?: HTMLInputElement): void {
    this.clearSelectedFile(inputElement);
  }

  // --- Trigger Generation: Main entry point ---
  onGenerateTemplate(): void {
    this.generateAiTemplate();
  }

  generateAiTemplate(): void {
    if (this.isGeneratingAi) return;

    const moduleName = this.normalizeModuleName(this.selectedAiModuleName);
    const entityId = Number(this.selectedAiEntityId || 0);
    const templateType = String(this.selectedAiTemplateType || "").trim();

    if (!moduleName) {
      this.toastr.warning("Please select a Module.", "Required");
      return;
    }

    if (entityId <= 0) {
      this.toastr.warning(`Please select a ${this.aiEntityLabel}.`, "Required");
      return;
    }

    if (!templateType) {
      this.toastr.warning("Please select a Template Type.", "Required");
      return;
    }

    const customPrompt = this.aiCustomPrompt?.trim() || this.aiForm.value.customPrompt?.trim() || "";
    this.aiForm.patchValue({ customPrompt: customPrompt });
    const htmlDesign = this.uploadedHtmlContent?.trim() || "";

    if (this.templateMode === 'new' && !htmlDesign) {
      this.fileUploadError = "Please upload a new .html design file.";
      this.toastr.warning(this.fileUploadError, "Required Design");
      return;
    }

    if (this.templateMode === 'modify' && !htmlDesign && !customPrompt) {
      this.toastr.warning("Please provide either a custom instruction prompt or upload a new .html design to modify the template.", "Instructions Required");
      return;
    }

    this.aiErrorMessage = "";
    this.aiSuccessResult = null;
    this.previewCertificateHtml = "";

    this.startLoadingAnimation();

    if (this.templateMode === 'new') {
      const payload: DynamicSqlTemplateGenerationRequestDTO = {
        masterType: templateType,
        moduleName: moduleName,
        moduleId: entityId,
        masterId: entityId,
        currentTemplateHTML: htmlDesign,
        customPrompt: customPrompt || null,
      };

      this.masterPrintMasterService
        .PrintMaster_DynamicSqlTemplateGeneration(payload)
        .pipe(
          timeout(180000),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (res: DynamicSqlTemplateGenerationResponseDTO) => {
            this.handleGenerationSuccess(res);
          },
          error: (err: any) => {
            this.handleGenerationError(err);
          },
        });
    } else {
      const payload: UpdateSqlTemplateRequestDTO = {
        masterType: templateType,
        moduleName: moduleName,
        moduleId: entityId,
        masterId: entityId,
        printId: this.existingPrintId > 0 ? this.existingPrintId : null,
        newTemplateHTML: htmlDesign || null,
        customPrompt: customPrompt || null,
      };

      this.masterPrintMasterService
        .PrintMaster_UpdateSqlTemplate(payload)
        .pipe(
          timeout(180000),
          takeUntil(this.destroy$)
        )
        .subscribe({
          next: (res: UpdateSqlTemplateResponseDTO) => {
            this.handleGenerationSuccess(res);
          },
          error: (err: any) => {
            this.handleGenerationError(err);
          },
        });
    }
  }

  private handleGenerationSuccess(res: any): void {
    this.stopLoadingAnimation();
    this.loadingProgress = 100;
    this.isGeneratingAi = false;
    this.aiSuccessResult = res;
    this.aiErrorMessage = "";

    const msg = res.message || "Dynamic SQL template generated and saved successfully.";
    this.toastr.success(msg, "Template Generated & Saved");

    // Automatically render live preview
    if (res.generatedSql) {
      this.renderGeneratedTemplatePreview(res.generatedSql);
    }
  }

  private handleGenerationError(err: any): void {
    this.stopLoadingAnimation();
    this.isGeneratingAi = false;
    console.error("Dynamic SQL Template Generation error:", err);

    let errorDetail = "An unexpected error occurred during template generation.";
    if (err?.name === "TimeoutError") {
      errorDetail = "Generation request timed out after 3 minutes. Please try again.";
    } else if (err?.error?.detail) {
      errorDetail = err.error.detail;
    } else if (err?.error?.message) {
      errorDetail = err.error.message;
    } else if (err?.error?.Message) {
      errorDetail = err.error.Message;
    } else if (typeof err?.error === "string") {
      errorDetail = err.error;
    } else if (err?.message) {
      errorDetail = err.message;
    }

    this.aiErrorMessage = errorDetail;
    this.toastr.error(errorDetail, "Generation Failed");
  }

  // --- Loading Animation Controller ---
  private startLoadingAnimation(): void {
    this.isGeneratingAi = true;
    this.loadingProgress = 8;
    this.loadingPhase = 1;
    this.loadingPhaseText = "Inspecting module schema & referencing default template...";

    const phases = [
      { at: 18, phase: 2, text: "Groq AI (openai/gpt-oss-20b) synthesizing dynamic SQL with entity tokens..." },
      { at: 52, phase: 3, text: "Validating SQL syntax, temporary tables & NVARCHAR format..." },
      { at: 78, phase: 4, text: "Registering validated template in module database..." },
      { at: 92, phase: 4, text: "Finalizing and verifying template storage..." },
    ];

    if (this.loadingTimer) {
      clearInterval(this.loadingTimer);
    }

    this.loadingTimer = setInterval(() => {
      if (!this.isGeneratingAi) {
        clearInterval(this.loadingTimer);
        return;
      }

      if (this.loadingProgress < 94) {
        this.loadingProgress += Math.floor(Math.random() * 3) + 1;
        for (const p of phases) {
          if (this.loadingProgress >= p.at && this.loadingPhase < p.phase) {
            this.loadingPhase = p.phase;
            this.loadingPhaseText = p.text;
          }
        }
      }
    }, 900);
  }

  private stopLoadingAnimation(): void {
    if (this.loadingTimer) {
      clearInterval(this.loadingTimer);
      this.loadingTimer = null;
    }
  }

  // --- Render Preview of Generated SQL ---
  renderGeneratedTemplatePreview(sql: string): void {
    if (!sql) return;

    this.isPreviewLoading = true;
    const tokenizedSql = this.applyAiTokens(sql);

    const payload: PrintMasterPrintDynamicSQLRequestDTO = {
      dynamicSQL: tokenizedSql,
    };

    this.masterPrintMasterService.PrintMaster_PrintDynamicSQL(payload).subscribe({
      next: (res) => {
        this.isPreviewLoading = false;
        const html = this.getPrintHtml(res);
        this.previewCertificateHtml = html.trim() ? html : "";
      },
      error: (err) => {
        this.isPreviewLoading = false;
        console.warn("Preview render warning:", err);
      }
    });
  }

  viewAiPreviewInNewTab(): void {
    const htmlToPreview = this.previewCertificateHtml || this.uploadedHtmlContent;
    if (!htmlToPreview?.trim()) {
      this.toastr.warning("No preview content available.");
      return;
    }

    const finalHtml = this.buildPreviewDocument(htmlToPreview);
    const blob = new Blob([finalHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");

    if (!newWindow) {
      URL.revokeObjectURL(url);
      this.toastr.warning("Please allow popups to preview the certificate.");
      return;
    }

    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  downloadAiPreviewHtml(): void {
    const htmlToDownload = this.previewCertificateHtml || this.uploadedHtmlContent;
    if (!htmlToDownload?.trim()) {
      this.toastr.warning("No preview content to download.");
      return;
    }

    const finalHtml = this.buildPreviewDocument(htmlToDownload);
    const blob = new Blob([finalHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const entityName = this.getSelectedAiEntityName() || this.aiEntityLabel;
    const templateName = this.selectedAiTemplateType || "Certificate";

    anchor.href = url;
    anchor.download = `${this.toSafeFileName(entityName)}-${this.toSafeFileName(templateName)}-Generated.html`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  copyGeneratedSql(sql?: string): void {
    const content = sql || this.aiSuccessResult?.generatedSql;
    if (!content) return;

    navigator.clipboard.writeText(content).then(
      () => {
        this.toastr.success("Generated SQL copied to clipboard.", "Copied");
      },
      () => {
        this.toastr.warning("Could not copy SQL to clipboard.");
      }
    );
  }

  // ==============================================================
  // HELPER METHODS
  // ==============================================================

  get entityLabel(): string {
    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    if (moduleName === "Hospital") return "Hospital";
    if (moduleName === "Society") return "Housing Society";
    return "School";
  }

  get aiEntityLabel(): string {
    const moduleName = this.normalizeModuleName(this.selectedAiModuleName);
    if (moduleName === "Hospital") return "Hospital";
    if (moduleName === "Society") return "Housing Society";
    return "School";
  }

  get baseTemplateIdLabel(): string {
    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    if (moduleName === "Hospital") return "HospitalId 0";
    if (moduleName === "Society") return "SocietyId 0";
    return "SchoolId 0";
  }

  get headerPlaceholder(): string {
    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    if (moduleName === "Hospital") {
      return (
        '<div class="certificate-header">\n' +
        "  <h2>{{HospitalName}}</h2>\n" +
        "  <p>{{HospitalAddress}}, {{City}}, {{State}}</p>\n" +
        "</div>"
      );
    }
    if (moduleName === "Society") {
      return (
        '<div class="certificate-header">\n' +
        "  <h2>{{SocietyName}}</h2>\n" +
        "  <p>{{SocietyAddress}}, {{City}}, {{State}} - {{ZipCode}}</p>\n" +
        "</div>"
      );
    }
    return (
      '<div class="certificate-header">\n' +
      "  <h2>{{SchoolName}}</h2>\n" +
      "  <p>{{SchoolAddress}}, {{City}}, {{State}} - {{Pincode}}</p>\n" +
      "</div>"
    );
  }

  getEntityId(entity: any): number {
    const moduleName = this.normalizeModuleName(this.selectedModuleName || this.selectedAiModuleName);
    if (moduleName === "Hospital") {
      return Number(entity?.hospitalId ?? entity?.HospitalId ?? 0);
    }
    if (moduleName === "Society") {
      return Number(entity?.societyId ?? entity?.SocietyId ?? 0);
    }
    return Number(entity?.schoolId ?? entity?.SchoolId ?? 0);
  }

  getEntityName(entity: any): string {
    if (!entity) return "";
    const moduleName = this.normalizeModuleName(this.selectedModuleName || this.selectedAiModuleName);
    if (moduleName === "Hospital") {
      return String(entity?.hospitalName ?? entity?.HospitalName ?? "").trim();
    }
    if (moduleName === "Society") {
      return String(entity?.societyName ?? entity?.SocietyName ?? "").trim();
    }
    return String(entity?.schoolName ?? entity?.SchoolName ?? "").trim();
  }

  getSelectedEntity(): any {
    const entityId = Number(this.selectedEntityId || 0);
    return this.entityList.find((entity: any) => this.getEntityId(entity) === entityId) || null;
  }

  getSelectedAiEntity(): any {
    const entityId = Number(this.selectedAiEntityId || 0);
    return this.aiEntityList.find((entity: any) => this.getEntityId(entity) === entityId) || null;
  }

  getSelectedAiEntityName(): string {
    return this.getEntityName(this.getSelectedAiEntity());
  }

  private normalizeModuleName(value: any): string {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "school" || normalized.includes("school")) return "School";
    if (normalized === "hospital" || normalized.includes("hospital")) return "Hospital";
    if (normalized === "society" || normalized.includes("society")) return "Society";
    return "";
  }

  private getResponseItems(response: any): any[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.Items)) return response.Items;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.Data)) return response.Data;
    return [];
  }

  private extractTemplateSql(response: any): string {
    if (!response) return "";
    if (typeof response === "string") return response;
    const items = this.getResponseItems(response);
    if (items.length > 0) {
      const item = items[0];
      const pContent = String(item?.pContent ?? item?.PContent ?? item?.htmlData ?? item?.HtmlData ?? "");
      if (pContent.trim()) return pContent;
    }
    return "";
  }

  private getPrintHtml(response: any): string {
    if (!response) return "";
    const items = Array.isArray(response?.items)
      ? response.items
      : Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response)
      ? response
      : [];
    return items
      .map((item: any) => String(item?.htmlData ?? item?.HtmlData ?? item?.pContent ?? item?.PContent ?? ""))
      .join("");
  }

  private applyModuleTokens(templateSql: string): string {
    if (!templateSql) return "";
    const moduleName = this.normalizeModuleName(this.selectedModuleName);
    const entity = this.getSelectedEntity();
    const moduleId = Number(this.selectedEntityId || 0);
    return this.replaceTokensInternal(templateSql, moduleName, entity, moduleId);
  }

  private applyAiTokens(templateSql: string): string {
    if (!templateSql) return "";
    const moduleName = this.normalizeModuleName(this.selectedAiModuleName);
    const entity = this.getSelectedAiEntity();
    const moduleId = Number(this.selectedAiEntityId || 0);
    return this.replaceTokensInternal(templateSql, moduleName, entity, moduleId);
  }

  private replaceTokensInternal(templateSql: string, moduleName: string, entity: any, moduleId: number): string {
    const entityName = this.prepareTokenText(this.getEntityName(entity));
    const address = this.prepareTokenText(
      this.getFirstAvailableText(entity, [
        "schoolAddress", "SchoolAddress", "hospitalAddress", "HospitalAddress",
        "societyAddress", "SocietyAddress", "address", "Address", "address1", "Address1",
      ])
    );
    const city = this.prepareTokenText(this.getFirstAvailableText(entity, ["city", "City"]));
    const state = this.prepareTokenText(this.getFirstAvailableText(entity, ["state", "State"]));
    const country = this.prepareTokenText(this.getFirstAvailableText(entity, ["country", "Country"]));
    const pincode = this.prepareTokenText(
      this.getFirstAvailableText(entity, ["pincode", "Pincode", "pinCode", "PinCode", "zipCode", "ZipCode"])
    );
    const phoneNo = this.prepareTokenText(
      this.getFirstAvailableText(entity, ["phoneNo", "PhoneNo", "mobileNo", "MobileNo"])
    );
    const email = this.prepareTokenText(this.getFirstAvailableText(entity, ["email", "Email", "emailId", "EmailId"]));

    const replacements: Array<[RegExp, string]> = [
      [/{{ModuleName}}/g, moduleName],
      [/{{ModuleId}}/g, moduleId.toString()],
      [/{{MasterId}}/g, moduleId.toString()],
      [/{{SchoolId}}/g, moduleName === "School" ? moduleId.toString() : ""],
      [/{{HospitalId}}/g, moduleName === "Hospital" ? moduleId.toString() : ""],
      [/{{SocietyId}}/g, moduleName === "Society" ? moduleId.toString() : ""],
      [/{{MasterName}}/g, entityName],
      [/{{SchoolName}}/g, moduleName === "School" ? entityName : ""],
      [/{{HospitalName}}/g, moduleName === "Hospital" ? entityName : ""],
      [/{{SocietyName}}/g, moduleName === "Society" ? entityName : ""],
      [/{{Address}}/g, address],
      [/{{SchoolAddress}}/g, moduleName === "School" ? address : ""],
      [/{{HospitalAddress}}/g, moduleName === "Hospital" ? address : ""],
      [/{{SocietyAddress}}/g, moduleName === "Society" ? address : ""],
      [/{{City}}/g, city],
      [/{{State}}/g, state],
      [/{{Country}}/g, country],
      [/{{Pincode}}/g, pincode],
      [/{{ZipCode}}/g, pincode],
      [/{{PhoneNo}}/g, phoneNo],
      [/{{Email}}/g, email],
    ];

    let finalTemplate = templateSql;
    replacements.forEach(([pattern, value]) => {
      finalTemplate = finalTemplate.replace(pattern, value);
    });
    return finalTemplate;
  }

  private getFirstAvailableText(source: any, keys: string[]): string {
    if (!source || typeof source !== "object") return "";
    for (const key of keys) {
      const value = source[key];
      if (value !== null && value !== undefined && String(value).trim()) {
        return String(value).trim();
      }
    }
    return "";
  }

  private prepareTokenText(value: any): string {
    if (value === null || value === undefined) return "";
    return String(value).replace(/'/g, "''");
  }

  private buildPreviewDocument(certificateHtml: string): string {
    const baseTag = `<base href="${window.location.origin}/">`;
    if (/<head[^>]*>/i.test(certificateHtml)) {
      return certificateHtml.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
    }
    const title = `${this.aiEntityLabel || this.entityLabel} ${this.selectedAiTemplateType || this.selectedTemplateId || "Certificate"}`;
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  ${baseTag}
  <style>
    body { margin: 0; padding: 24px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f8fafc; }
    .certificate-container { max-width: 900px; margin: 0 auto; background: #ffffff; padding: 32px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
  </style>
</head>
<body>
  <div class="certificate-container">
    ${certificateHtml}
  </div>
</body>
</html>
    `.trim();
  }

  private toSafeFileName(value: string): string {
    return (value || "certificate")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  }
}
